import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Users, 
  Trophy, 
  Medal,
  Crown,
  Star,
  Calendar,
  MapPin,
  Clock,
  ArrowRight,
  Loader2,
  Shield,
  Target
} from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_matchfinder-25/artifacts/wk49ezk2_Logo.jpg";
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get initials
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
};

export default function Community() {
  const [teams, setTeams] = useState([]);
  const [topPlayers, setTopPlayers] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('teams');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all data in parallel
        const [teamsRes, leaderboardRes, matchesRes] = await Promise.all([
          axios.get(`${API_URL}/api/teams`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/users/leaderboard`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/matches`).catch(() => ({ data: [] }))
        ]);
        
        setTeams(teamsRes.data || []);
        setTopPlayers((leaderboardRes.data || []).slice(0, 10));
        
        // Sort matches: confirmed results first, then by date
        const allMatches = matchesRes.data || [];
        const sortedMatches = allMatches.sort((a, b) => {
          // Confirmed results first
          if (a.result_status === 'confirmed' && b.result_status !== 'confirmed') return -1;
          if (b.result_status === 'confirmed' && a.result_status !== 'confirmed') return 1;
          // Then by date (newest first)
          return new Date(b.date || 0) - new Date(a.date || 0);
        });
        setRecentMatches(sortedMatches.slice(0, 20));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const getRankIcon = (index) => {
    if (index === 0) return <Crown className="w-5 h-5 text-amber-500" />;
    if (index === 1) return <Medal className="w-5 h-5 text-slate-400" />;
    if (index === 2) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-slate-500">{index + 1}</span>;
  };

  const getRankBg = (index) => {
    if (index === 0) return 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200';
    if (index === 1) return 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200';
    if (index === 2) return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200';
    return 'bg-white border-slate-200';
  };

  const getMatchStatusBadge = (status, resultStatus) => {
    if (resultStatus === 'confirmed') {
      return <Badge className="bg-green-100 text-green-700">✓ Ergebnis</Badge>;
    }
    if (resultStatus === 'submitted') {
      return <Badge className="bg-purple-100 text-purple-700">Zur Prüfung</Badge>;
    }
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-700">Beendet</Badge>;
      case 'awaiting_result':
        return <Badge className="bg-orange-100 text-orange-700">Ergebnis fehlt</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-700">Läuft</Badge>;
      case 'scheduled':
        return <Badge className="bg-amber-100 text-amber-700">Geplant</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img src={LOGO_URL} alt="Tunis Kick" className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover" />
              <span className="font-display font-bold text-lg sm:text-xl text-tunis-blue">Tunis Kick</span>
            </Link>
            <div className="flex items-center gap-1 sm:gap-3">
              <Link to="/about">
                <Button variant="ghost" className="font-semibold text-tunis-blue hidden sm:inline-flex">
                  Über uns
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="font-semibold text-tunis-blue text-sm sm:text-base px-2 sm:px-4">
                  Anmelden
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-3 sm:px-6 text-sm sm:text-base">
                  Registrieren
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 sm:pt-28 pb-8 sm:pb-12 bg-gradient-to-r from-tunis-blue to-tunis-blue/90">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-3 sm:mb-4">
            Community Übersicht
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-sm sm:text-base">
            Entdecke die besten Teams, Spieler und aktuelle Spiele in der Tunis Kick Community
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="sticky top-14 sm:top-16 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 py-2 overflow-x-auto scrollbar-none">
            {[
              { id: 'teams', label: 'Teams', icon: Users, count: teams.length },
              { id: 'players', label: 'Top 10 Spieler', icon: Trophy, count: topPlayers.length },
              { id: 'matches', label: 'Spiele', icon: Calendar, count: recentMatches.length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-medium text-xs sm:text-sm transition-all whitespace-nowrap flex-shrink-0
                  ${activeTab === tab.id 
                    ? 'bg-tunis-blue text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                  }`}
              >
                <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">{tab.label}</span>
                <span className="xs:hidden">{tab.id === 'players' ? 'Top 10' : tab.label.split(' ')[0]}</span>
                <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs
                  ${activeTab === tab.id 
                    ? 'bg-white/20 text-white' 
                    : 'bg-slate-200 text-slate-600'
                  }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
            </div>
          ) : (
            <>
              {/* Teams Tab */}
              {activeTab === 'teams' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-display font-bold text-2xl text-tunis-blue">
                      Alle Teams ({teams.length})
                    </h2>
                  </div>
                  
                  {teams.length === 0 ? (
                    <Card className="text-center py-12">
                      <CardContent>
                        <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">Noch keine Teams vorhanden</p>
                        <Link to="/register" className="mt-4 inline-block">
                          <Button className="btn-primary">Erstelle das erste Team</Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {teams.map((team, index) => (
                        <Card key={team.id} className="hover:shadow-lg transition-shadow border-slate-200">
                          <CardContent className="p-5">
                            <div className="flex items-start gap-4">
                              <Avatar className="w-14 h-14 border-2 border-slate-200">
                                <AvatarImage src={team.logo_url} alt={team.name} />
                                <AvatarFallback className="bg-gradient-to-br from-tunis-blue to-tunis-teal text-white font-bold text-xl">
                                  {team.name?.charAt(0) || 'T'}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-tunis-blue truncate">{team.name}</h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <Avatar className="w-5 h-5">
                                    <AvatarImage src={team.captain_avatar} />
                                    <AvatarFallback className="bg-tunis-teal text-white text-xs">
                                      {getInitials(team.captain_name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <p className="text-sm text-slate-500 truncate">
                                    {team.captain_name || 'Unbekannt'}
                                  </p>
                                </div>
                                <div className="flex items-center gap-3 mt-2">
                                  <span className="text-xs text-slate-500 flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {team.members?.length || 0} / {team.max_members || 11}
                                  </span>
                                  <span className="text-xs text-tunis-teal font-semibold flex items-center gap-1">
                                    <Trophy className="w-3 h-3" />
                                    {team.avg_elo || team.team_elo || 1000} ELO
                                  </span>
                                </div>
                                {/* Team Members Avatars */}
                                {team.members && team.members.length > 0 && (
                                  <div className="flex -space-x-2 mt-3">
                                    {team.members.slice(0, 5).map((member, idx) => (
                                      <Avatar key={idx} className="w-7 h-7 border-2 border-white">
                                        <AvatarImage src={member.avatar_url} />
                                        <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                                          {getInitials(member.name)}
                                        </AvatarFallback>
                                      </Avatar>
                                    ))}
                                    {team.members.length > 5 && (
                                      <div className="w-7 h-7 bg-slate-200 border-2 border-white rounded-full flex items-center justify-center text-xs text-slate-600 font-medium">
                                        +{team.members.length - 5}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Top Players Tab */}
              {activeTab === 'players' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-display font-bold text-2xl text-tunis-blue">
                      Top 10 Spieler
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Trophy className="w-4 h-4 text-tunis-teal" />
                      Nach ELO-Ranking
                    </div>
                  </div>
                  
                  {topPlayers.length === 0 ? (
                    <Card className="text-center py-12">
                      <CardContent>
                        <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">Noch keine Spieler im Ranking</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {topPlayers.map((player, index) => (
                        <div 
                          key={player.id}
                          className={`flex items-center gap-4 p-4 rounded-xl border ${getRankBg(index)} transition-all hover:shadow-md`}
                        >
                          <div className="w-10 h-10 flex items-center justify-center">
                            {getRankIcon(index)}
                          </div>
                          <Avatar className="w-12 h-12 border-2 border-white shadow-sm">
                            <AvatarImage src={player.avatar_url} alt={player.name} />
                            <AvatarFallback className="bg-tunis-blue text-white font-bold">
                              {getInitials(player.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h3 className="font-bold text-tunis-blue">{player.name}</h3>
                            <p className="text-sm text-slate-500">
                              {player.matches_played || 0} Spiele • {player.wins || 0} Siege
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-lg text-tunis-teal">{player.elo_rating || player.elo_points || 1000}</div>
                            <div className="text-xs text-slate-500">ELO</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Matches Tab */}
              {activeTab === 'matches' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-display font-bold text-2xl text-tunis-blue">
                      Spiele & Ergebnisse
                    </h2>
                    <div className="text-sm text-muted-foreground">
                      {recentMatches.filter(m => m.result_status === 'confirmed').length} mit Ergebnis
                    </div>
                  </div>
                  
                  {recentMatches.length === 0 ? (
                    <Card className="text-center py-12">
                      <CardContent>
                        <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">Noch keine Spiele vorhanden</p>
                        <Link to="/register" className="mt-4 inline-block">
                          <Button className="btn-primary">Erstelle das erste Spiel</Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-4">
                      {recentMatches.map((match) => {
                        // Split participants into two "teams" for display
                        const participants = match.participants || [];
                        const midPoint = Math.ceil(participants.length / 2);
                        const homeTeam = participants.slice(0, midPoint);
                        const awayTeam = participants.slice(midPoint);
                        
                        return (
                          <Card key={match.id} className={`overflow-hidden ${match.result_status === 'confirmed' ? 'ring-2 ring-green-400' : ''}`}>
                            {/* Match Header */}
                            <div className="bg-gradient-to-r from-tunis-blue to-tunis-blue/90 px-4 py-2 flex items-center justify-between">
                              <div className="flex items-center gap-2 text-white/80 text-xs">
                                <Calendar className="w-3 h-3" />
                                {match.date} • {match.time}
                              </div>
                              <div className="flex items-center gap-2">
                                {getMatchStatusBadge(match.status, match.result_status)}
                              </div>
                            </div>
                            
                            <CardContent className="p-0">
                              {/* Scoreboard Layout */}
                              {match.result_status === 'confirmed' ? (
                                <div className="flex items-center justify-between p-4 sm:p-6">
                                  {/* Home Team */}
                                  <div className="flex-1 flex flex-col items-center text-center">
                                    <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-tunis-blue to-tunis-blue/80 flex items-center justify-center mb-2 shadow-lg">
                                      {homeTeam[0]?.avatar_url ? (
                                        <img src={homeTeam[0].avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                                      ) : (
                                        <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                                      )}
                                    </div>
                                    <span className="font-bold text-tunis-blue text-sm sm:text-base">Heim</span>
                                    <span className="text-xs text-slate-500 truncate max-w-[100px]">
                                      {homeTeam[0]?.name || match.host_name || 'Team A'}
                                    </span>
                                    {homeTeam.length > 1 && (
                                      <div className="flex -space-x-2 mt-2">
                                        {homeTeam.slice(0, 3).map((p, i) => (
                                          <Avatar key={i} className="w-6 h-6 border-2 border-white">
                                            <AvatarFallback className="bg-tunis-teal text-white text-[10px]">
                                              {getInitials(p.name)}
                                            </AvatarFallback>
                                          </Avatar>
                                        ))}
                                        {homeTeam.length > 3 && (
                                          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold border-2 border-white">
                                            +{homeTeam.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Score */}
                                  <div className="flex flex-col items-center px-4 sm:px-8">
                                    <div className="flex items-center gap-3 sm:gap-6">
                                      <span className={`text-4xl sm:text-6xl font-black ${match.home_score > match.away_score ? 'text-green-600' : match.home_score < match.away_score ? 'text-slate-400' : 'text-tunis-blue'}`}>
                                        {match.home_score}
                                      </span>
                                      <span className="text-2xl sm:text-4xl font-bold text-slate-300">:</span>
                                      <span className={`text-4xl sm:text-6xl font-black ${match.away_score > match.home_score ? 'text-green-600' : match.away_score < match.home_score ? 'text-slate-400' : 'text-tunis-blue'}`}>
                                        {match.away_score}
                                      </span>
                                    </div>
                                    <span className="text-xs text-green-600 font-medium mt-2 bg-green-50 px-3 py-1 rounded-full">
                                      ✓ Endergebnis
                                    </span>
                                  </div>
                                  
                                  {/* Away Team */}
                                  <div className="flex-1 flex flex-col items-center text-center">
                                    <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-tunis-teal to-tunis-teal/80 flex items-center justify-center mb-2 shadow-lg">
                                      {awayTeam[0]?.avatar_url ? (
                                        <img src={awayTeam[0].avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                                      ) : (
                                        <Target className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                                      )}
                                    </div>
                                    <span className="font-bold text-tunis-blue text-sm sm:text-base">Gast</span>
                                    <span className="text-xs text-slate-500 truncate max-w-[100px]">
                                      {awayTeam[0]?.name || 'Team B'}
                                    </span>
                                    {awayTeam.length > 1 && (
                                      <div className="flex -space-x-2 mt-2">
                                        {awayTeam.slice(0, 3).map((p, i) => (
                                          <Avatar key={i} className="w-6 h-6 border-2 border-white">
                                            <AvatarFallback className="bg-tunis-blue text-white text-[10px]">
                                              {getInitials(p.name)}
                                            </AvatarFallback>
                                          </Avatar>
                                        ))}
                                        {awayTeam.length > 3 && (
                                          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold border-2 border-white">
                                            +{awayTeam.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                /* Non-confirmed match - same scoreboard layout with placeholder */
                                <div className="flex items-center justify-between p-4 sm:p-6">
                                  {/* Home Team */}
                                  <div className="flex-1 flex flex-col items-center text-center">
                                    <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-tunis-blue to-tunis-blue/80 flex items-center justify-center mb-2 shadow-lg">
                                      {homeTeam[0]?.avatar_url ? (
                                        <img src={homeTeam[0].avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                                      ) : (
                                        <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                                      )}
                                    </div>
                                    <span className="font-bold text-tunis-blue text-sm sm:text-base">Heim</span>
                                    <span className="text-xs text-slate-500 truncate max-w-[100px]">
                                      {homeTeam[0]?.name || match.host_name || 'Team A'}
                                    </span>
                                    {homeTeam.length > 1 && (
                                      <div className="flex -space-x-2 mt-2">
                                        {homeTeam.slice(0, 3).map((p, i) => (
                                          <Avatar key={i} className="w-6 h-6 border-2 border-white">
                                            <AvatarFallback className="bg-tunis-teal text-white text-[10px]">
                                              {getInitials(p.name)}
                                            </AvatarFallback>
                                          </Avatar>
                                        ))}
                                        {homeTeam.length > 3 && (
                                          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold border-2 border-white">
                                            +{homeTeam.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Pending Score */}
                                  <div className="flex flex-col items-center px-4 sm:px-8">
                                    <div className="flex items-center gap-3 sm:gap-6">
                                      <span className="text-4xl sm:text-6xl font-black text-slate-300">?</span>
                                      <span className="text-2xl sm:text-4xl font-bold text-slate-300">:</span>
                                      <span className="text-4xl sm:text-6xl font-black text-slate-300">?</span>
                                    </div>
                                    <span className={`text-xs font-medium mt-2 px-3 py-1 rounded-full ${
                                      match.result_status === 'submitted' 
                                        ? 'text-purple-600 bg-purple-50' 
                                        : match.status === 'awaiting_result'
                                        ? 'text-orange-600 bg-orange-50'
                                        : 'text-slate-500 bg-slate-100'
                                    }`}>
                                      {match.result_status === 'submitted' 
                                        ? '⏳ Zur Prüfung' 
                                        : match.status === 'awaiting_result'
                                        ? '⚠️ Ergebnis fehlt'
                                        : `${participants.length}/${match.max_players || 10} Spieler`
                                      }
                                    </span>
                                  </div>
                                  
                                  {/* Away Team */}
                                  <div className="flex-1 flex flex-col items-center text-center">
                                    <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-tunis-teal to-tunis-teal/80 flex items-center justify-center mb-2 shadow-lg">
                                      {awayTeam[0]?.avatar_url ? (
                                        <img src={awayTeam[0].avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                                      ) : (
                                        <Target className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                                      )}
                                    </div>
                                    <span className="font-bold text-tunis-blue text-sm sm:text-base">Gast</span>
                                    <span className="text-xs text-slate-500 truncate max-w-[100px]">
                                      {awayTeam[0]?.name || 'Team B'}
                                    </span>
                                    {awayTeam.length > 1 && (
                                      <div className="flex -space-x-2 mt-2">
                                        {awayTeam.slice(0, 3).map((p, i) => (
                                          <Avatar key={i} className="w-6 h-6 border-2 border-white">
                                            <AvatarFallback className="bg-tunis-blue text-white text-[10px]">
                                              {getInitials(p.name)}
                                            </AvatarFallback>
                                          </Avatar>
                                        ))}
                                        {awayTeam.length > 3 && (
                                          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold border-2 border-white">
                                            +{awayTeam.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Match Footer */}
                              <div className="bg-slate-50 px-4 py-2 flex items-center justify-between text-xs text-slate-500 border-t">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {match.field_name || 'TBA'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {match.duration_hours || 2}h Spielzeit
                                </span>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-2xl text-tunis-blue mb-4">
            Werde Teil der Community!
          </h2>
          <p className="text-slate-600 mb-6">
            Registriere dich kostenlos und spiele mit den besten Amateurfußballern in Tunis
          </p>
          <Link to="/register">
            <Button size="lg" className="btn-primary px-8">
              Jetzt kostenlos registrieren
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Tunis Kick" className="h-8 w-8 rounded-full object-cover" />
              <span className="font-display font-bold text-tunis-blue">Tunis Kick</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <Link to="/" className="hover:text-tunis-blue transition-colors">Startseite</Link>
              <Link to="/about" className="hover:text-tunis-blue transition-colors">Über uns</Link>
              <span className="text-tunis-teal font-medium">Community</span>
              <span>© 2024</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
